import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";

/**
 * HomeTablet — landscape AND portrait tablet composition (iPad in either
 * orientation, plus 4:3-ish landscape tablets).
 *
 * Why this file is special:
 *   • Desktop curves are tuned for 16:9 monitors with comfortable side
 *     margins around the 1:1 SVG square.
 *   • Mobile curves are tuned for ~0.45-aspect phone screens.
 *   • Tablets fall between both: 0.66 → 1.5 aspect. iPad portrait sits at
 *     0.75, iPad landscape at 1.33 — neither plays well with the desktop
 *     or mobile compositions.
 *
 *   We resolve this by detecting orientation at runtime and swapping in
 *   either a portrait or landscape SVG composition, each tuned to that
 *   shape. The shared brand chrome (PANDURATA wordmark, AI READABLE pill)
 *   stays identical in both.
 */

// ============ Landscape (1500×1080 viewBox, ~4:3 — iPad landscape) =========
const PATH_TL_ARTDEPT = "M 100 480 A 360 360 0 0 1 460 280";
const PATH_TL_ARCHITECTURE = "M 1080 380 C 1180 540, 1180 720, 1080 880";
const PATH_TL_REPOSITORY = "M 420 380 C 320 540, 320 720, 420 880";
const PATH_TL_MANIFESTO = "M 540 950 Q 750 1020 960 950";
const PATH_TL_LETSTALK = "M 1100 1020 A 320 320 0 0 1 1440 940";

// ============ Portrait (1000×1400 viewBox, 0.71 aspect — iPad portrait) ====
// All curves are kept tightly in the central 80% of the viewBox so the
// rendered labels stay clear of viewport edges and the Safari URL bar /
// toolbar regardless of the SVG-to-display scale.
//   - ART DEPT.    : gentle dome above the leaf, centered
//   - MANIFESTO    : vertical "(" curve along leaf's left edge
//   - ARCHITECTURE : vertical ")" curve along leaf's right edge
//   - REPOSITORY   : shallow arc under the leaf, centered
//   - LET'S TALK   : shorter arc lower-center, below REPOSITORY
const PATH_TP_ARTDEPT = "M 250 360 Q 500 260 750 360";
const PATH_TP_MANIFESTO = "M 300 480 C 240 620, 240 820, 300 960";
const PATH_TP_ARCHITECTURE = "M 700 480 C 760 620, 760 820, 700 960";
const PATH_TP_REPOSITORY = "M 280 1080 Q 500 1160 720 1080";
const PATH_TP_LETSTALK = "M 320 1240 Q 500 1300 680 1240";

const useOrientation = () => {
  const compute = () =>
    typeof window === "undefined" || window.innerWidth >= window.innerHeight
      ? "landscape"
      : "portrait";
  const [orientation, setOrientation] = useState(compute);
  useEffect(() => {
    const onResize = () => setOrientation(compute());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return orientation;
};

const HomeTablet = () => {
  const navigateTo = useNavigate();
  const navigate = (path) => navigateTo(path);
  const orientation = useOrientation();

  const onKey = (e, path) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  const isPortrait = orientation === "portrait";

  // Pick the right viewBox + curve coordinates for this orientation.
  const viewBox = isPortrait ? "0 0 1000 1400" : "0 0 1500 1080";
  const paths = isPortrait
    ? {
        artdept: PATH_TP_ARTDEPT,
        architecture: PATH_TP_ARCHITECTURE,
        manifesto: PATH_TP_MANIFESTO,
        repository: PATH_TP_REPOSITORY,
        letstalk: PATH_TP_LETSTALK,
      }
    : {
        artdept: PATH_TL_ARTDEPT,
        architecture: PATH_TL_ARCHITECTURE,
        manifesto: PATH_TL_MANIFESTO,
        repository: PATH_TL_REPOSITORY,
        letstalk: PATH_TL_LETSTALK,
      };

  return (
    <main className="home home--tablet" data-testid="home-page">
      <div className="home__scene" data-testid="home-scene">
        <h1 className="home__wordmark" data-testid="wordmark-pandurata">
          PANDURATA
        </h1>

        <svg
          className="home__svg"
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Pandurata navigation"
        >
          <defs>
            <path id="t-curve-artdept" d={paths.artdept} />
            <path id="t-curve-architecture" d={paths.architecture} />
            <path id="t-curve-manifesto" d={paths.manifesto} />
            <path id="t-curve-repository" d={paths.repository} />
            <path id="t-curve-letstalk" d={paths.letstalk} />
          </defs>

          {/* ART DEPT. */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Art Dept"
            onClick={() => navigate("/art-dept")}
            onKeyDown={(e) => onKey(e, "/art-dept")}
            data-testid="label-artdept"
          >
            <textPath href="#t-curve-artdept" startOffset="50%" textAnchor="middle">
              ART DEPT.
            </textPath>
          </text>

          {/* ARCHITECTURE */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Architecture"
            onClick={() => navigate("/architecture")}
            onKeyDown={(e) => onKey(e, "/architecture")}
            data-testid="label-architecture"
          >
            <textPath href="#t-curve-architecture" startOffset="50%" textAnchor="middle">
              ARCHITECTURE
            </textPath>
          </text>

          {/* MANIFESTO */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Manifesto"
            onClick={() => navigate("/manifesto")}
            onKeyDown={(e) => onKey(e, "/manifesto")}
            data-testid="label-manifesto"
          >
            <textPath href="#t-curve-manifesto" startOffset="50%" textAnchor="middle">
              MANIFESTO
            </textPath>
          </text>

          {/* REPOSITORY */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Repository"
            onClick={() => navigate("/repository")}
            onKeyDown={(e) => onKey(e, "/repository")}
            data-testid="label-repository"
          >
            <textPath href="#t-curve-repository" startOffset="50%" textAnchor="middle">
              REPOSITORY
            </textPath>
          </text>

          {/* LET'S TALK */}
          <text
            className="home__label"
            role="link"
            tabIndex={0}
            aria-label="Let's Talk"
            onClick={() => navigate("/lets-talk")}
            onKeyDown={(e) => onKey(e, "/lets-talk")}
            data-testid="label-letstalk"
          >
            <textPath href="#t-curve-letstalk" startOffset="50%" textAnchor="middle">
              LET&apos;S TALK
            </textPath>
          </text>
        </svg>
      </div>

      <Link
        to="/ai"
        className="ai-toggle"
        aria-label="Switch to AI-readable mode"
        data-testid="ai-toggle"
      >
        <Bot size={13} strokeWidth={1.7} />
        <span>AI READABLE</span>
      </Link>
    </main>
  );
};

export default HomeTablet;
